import { User } from '@/shared/types';

/**
 * Interfaz para los datos requeridos en el onboarding
 */
export interface OnboardingRequiredData {
  // Datos básicos (Paso 1)
  firstName: string;
  lastName: string;
  phone: string;
  company: string;
  
  // Preferencias (Paso 2)
  industry?: string;
  propertyTypes?: string[];
  budget?: string;
  experience?: string;
  role?: string;
}

/**
 * Resultado de la validación del onboarding
 */
export interface OnboardingValidationResult {
  isComplete: boolean;
  isBasicDataComplete: boolean;
  isPreferencesComplete: boolean;
  missingFields: string[];
  missingBasicFields: string[];
  missingPreferenceFields: string[];
  userHasAllData: boolean;
}

/**
 * Valida si un usuario tiene todos los datos básicos requeridos para el onboarding
 */
export const validateBasicOnboardingData = (user: User): {
  isComplete: boolean;
  missingFields: string[];
} => {
  const requiredFields: (keyof User)[] = ['firstName', 'lastName', 'phone'];
  const missingFields: string[] = [];

  // Verificar campos básicos del usuario
  requiredFields.forEach(field => {
    const value = user[field];
    if (!value || (typeof value === 'string' && value.trim().length < 2)) {
      missingFields.push(field);
    }
  });

  // Verificar que tenga empresa en el perfil
  const hasCompany = user.profile?.company && 
    typeof user.profile.company === 'string' && 
    user.profile.company.trim().length >= 2;

  if (!hasCompany) {
    missingFields.push('company');
  }

  return {
    isComplete: missingFields.length === 0,
    missingFields,
  };
};

/**
 * Valida si un usuario tiene todas las preferencias requeridas para el onboarding
 */
export const validatePreferenceData = (user: User): {
  isComplete: boolean;
  missingFields: string[];
} => {
  const missingFields: string[] = [];

  // Verificar que tenga rol asignado
  if (!user.role || user.role.trim() === '') {
    missingFields.push('role');
  }

  // Verificar preferencias en el perfil
  const profile = user.profile || {};
  
  // Industria
  if (!profile.industry || typeof profile.industry !== 'string' || profile.industry.trim() === '') {
    missingFields.push('industry');
  }

  // Tipos de propiedades
  if (!profile.propertyTypes || !Array.isArray(profile.propertyTypes) || profile.propertyTypes.length === 0) {
    missingFields.push('propertyTypes');
  }

  // Presupuesto
  if (!profile.budget || typeof profile.budget !== 'string' || profile.budget.trim() === '') {
    missingFields.push('budget');
  }

  // Experiencia
  if (!profile.experience || typeof profile.experience !== 'string' || profile.experience.trim() === '') {
    missingFields.push('experience');
  }

  return {
    isComplete: missingFields.length === 0,
    missingFields,
  };
};

/**
 * Función principal que valida si un usuario tiene todos los datos requeridos para el onboarding
 */
export const validateOnboardingData = (user: User): OnboardingValidationResult => {
  const basicValidation = validateBasicOnboardingData(user);
  const preferencesValidation = validatePreferenceData(user);

  const allMissingFields = [
    ...basicValidation.missingFields,
    ...preferencesValidation.missingFields,
  ];

  return {
    isComplete: basicValidation.isComplete && preferencesValidation.isComplete,
    isBasicDataComplete: basicValidation.isComplete,
    isPreferencesComplete: preferencesValidation.isComplete,
    missingFields: allMissingFields,
    missingBasicFields: basicValidation.missingFields,
    missingPreferenceFields: preferencesValidation.missingFields,
    userHasAllData: allMissingFields.length === 0,
  };
};

/**
 * Determina si un usuario debe ser redirigido al onboarding
 * Considera tanto el flag onboardingCompleted como los datos reales del usuario
 * Los usuarios SUPER_ADMIN nunca son redirigidos al onboarding
 */
export const shouldRedirectToOnboarding = (user: User | null): boolean => {
  if (!user) {
    return false; // No hay usuario, no redirigir
  }

  // Los super admins nunca necesitan completar onboarding
  if (user.role === 'SUPER_ADMIN') {
    return false;
  }

  // Si ya completó el onboarding según el flag, no redirigir
  if (user.onboardingCompleted === true) {
    return false;
  }

  // Si el flag es false o undefined, SIEMPRE redirigir al onboarding
  // independientemente de si tiene datos o no, porque el flag indica que no ha completado el proceso
  return true;
};

/**
 * Determina si un usuario puede saltarse el onboarding automáticamente
 * (porque ya tiene todos los datos necesarios)
 */
export const canSkipOnboarding = (user: User | null): boolean => {
  if (!user) {
    return false;
  }

  const validation = validateOnboardingData(user);
  return validation.userHasAllData && user.onboardingCompleted !== true;
};

/**
 * Obtiene los datos del usuario en el formato esperado por el formulario de onboarding
 */
export const extractOnboardingDataFromUser = (user: User): OnboardingRequiredData => {
  const profile = user.profile || {};
  
  return {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    company: profile.company || '',
    industry: profile.industry || '',
    propertyTypes: Array.isArray(profile.propertyTypes) ? profile.propertyTypes : [],
    budget: profile.budget || '',
    experience: profile.experience || '',
    role: user.role || '',
  };
};

/**
 * Valida si los datos del onboarding están completos según el esquema de validación
 */
export const isOnboardingDataValid = (data: OnboardingRequiredData): boolean => {
  // Validaciones básicas
  const hasBasicData = 
    data.firstName && data.firstName.length >= 2 &&
    data.lastName && data.lastName.length >= 2 &&
    data.phone && data.phone.length >= 10 &&
    data.company && data.company.length >= 2;

  // Validaciones de preferencias
  const hasPreferences = 
    data.role && data.role.trim() !== '' &&
    data.industry && data.industry.trim() !== '' &&
    data.propertyTypes && data.propertyTypes.length > 0 &&
    data.budget && data.budget.trim() !== '' &&
    data.experience && data.experience.trim() !== '';

  return Boolean(hasBasicData && hasPreferences);
};

/**
 * Utilidad para logging de validación (útil para debugging)
 */
export const logOnboardingValidation = (user: User, result: OnboardingValidationResult): void => {
  // Validation logging removed
};
